/**
 * LootPop Context
 *
 * Provides a centralized way to trigger loot pop animations from anywhere in the app.
 * Manages multiple simultaneous pops and their lifecycle.
 *
 * @example
 * const { triggerLootPop } = useLootPop();
 *
 * triggerLootPop({
 *   item: { name: "Wood", rarity: "common", quantity: 5 },
 *   position: { x: 500, y: 300 } // optional, defaults to center
 * });
 */

import React, { createContext, useContext, useState, useCallback } from 'react';
import LootPop from '../components/celebrations/LootPop';

const LootPopContext = createContext();

export function LootPopProvider({ children }) {
  const [lootPops, setLootPops] = useState([]);

  const triggerLootPop = useCallback((config) => {
    const id = Date.now() + Math.random();

    // Default position to center of viewport if not provided
    const position = config.position || {
      x: window.innerWidth / 2,
      y: window.innerHeight / 2
    };

    const newPop = {
      id,
      item: config.item,
      position
    };

    setLootPops(prev => [...prev, newPop]);

    // Auto-remove after animation completes
    setTimeout(() => {
      setLootPops(prev => prev.filter(pop => pop.id !== id));
    }, 1500);
  }, []);

  const removeLootPop = useCallback((id) => {
    setLootPops(prev => prev.filter(pop => pop.id !== id));
  }, []);

  return (
    <LootPopContext.Provider value={{ triggerLootPop }}>
      {children}
      {/* Render all active loot pops */}
      <div className="fixed inset-0 pointer-events-none z-notification">
        {lootPops.map(pop => (
          <LootPop
            key={pop.id}
            item={pop.item}
            position={pop.position}
            onComplete={() => removeLootPop(pop.id)}
          />
        ))}
      </div>
    </LootPopContext.Provider>
  );
}

export function useLootPop() {
  const context = useContext(LootPopContext);
  if (!context) {
    throw new Error('useLootPop must be used within a LootPopProvider');
  }
  return context;
}

export default LootPopProvider;
